import React, { useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import moment from "moment";
import { Backdrop, Box, Grid } from "@material-ui/core";
import { PrintSharp } from "@material-ui/icons";
import CommonAutoCompleteField from "src/components/CommonAutoCompleteField/CommonAutoCompleteField";
import CommonButton from "src/components/CommonButton/CommonButton";
import NewCommonButton from "src/components/CommonButton/NewCommonButton";
import CommonDataGridPage from "src/components/CommonDataGridPage/CommonDataGridPage";
import CommonLoader from "src/components/Loader/CommonLoader";
import CommonMaterialGrid from "src/components/CommonXGrid/CommonMaterialGrid";
import { fetchWrapper } from "src/api-client/fetchWrapper";
import { getPrintFile } from "src/app-redux/common-actions";
import { addNotificationAction } from "src/app-redux/settings/settingsSlice";
import { NodCommercialRoute } from "../../nod-commercial.routes";
import { useStyles } from "../PaymentsList/components/style";
import { columns } from "./components/columns";

function parseInvoiceHistoryResponse(results: any): any[] {
  const raw =
    results?.value?.records ??
    results?.records ??
    results?.value?.invoiceHistory ??
    results?.value ??
    results?.invoiceHistory ??
    results;

  return Array.isArray(raw) ? raw : [];
}

function buildContainerChassisNumbersFromRows(
  targetRow: any,
  allRows: any[]
): { chassisNumber: string; containerNumber: string }[] {
  const fromApi = targetRow?.containerChassisNumbers;
  if (Array.isArray(fromApi) && fromApi.length) {
    const containerNo = String(targetRow?.containerNo ?? targetRow?.containerNumber ?? "").trim();
    return fromApi.map((item: any) => ({
      chassisNumber: String(item?.chassisNumber ?? item?.chasisNumber ?? "").trim(),
      containerNumber: String(item?.containerNumber ?? item?.containerNo ?? containerNo).trim(),
    }));
  }

  const gatePassId = targetRow?.gatePassId;
  const containerNo = String(targetRow?.containerNo ?? targetRow?.containerNumber ?? "").trim();
  const relatedRows =
    gatePassId != null && gatePassId !== ""
      ? allRows.filter((row) => String(row?.gatePassId) === String(gatePassId))
      : allRows.filter(
          (row) =>
            String(row?.containerNo ?? row?.containerNumber ?? "").trim() === containerNo &&
            String(row?.indexNo ?? "") === String(targetRow?.indexNo ?? "") &&
            String(row?.virNo ?? "") === String(targetRow?.virNo ?? "")
        );

  const seen = new Set<string>();
  const mapped = relatedRows
    .map((row) => {
      const chassisNumber = String(row?.chassisNumbers ?? row?.chassisNumber ?? "").trim();
      const key = `${containerNo}|${chassisNumber}`;
      if (chassisNumber && seen.has(key)) return null;
      if (chassisNumber) seen.add(key);
      return {
        chassisNumber,
        containerNumber: String(row?.containerNo ?? row?.containerNumber ?? containerNo).trim(),
      };
    })
    .filter(Boolean) as { chassisNumber: string; containerNumber: string }[];

  if (mapped.length) return mapped;

  return containerNo ? [{ containerNumber: containerNo, chassisNumber: "" }] : [];
}

export function GatePassInvoiceHistory(): React.ReactElement {
  const classes = useStyles();
  const dispatch = useDispatch();
  const moduleName = NodCommercialRoute.title;
  const pageHeading = "GatePass Invoice History";
  const isExport = localStorage.getItem("isAllowToExportData") === "true";

  const [loader, setLoader] = useState(false);
  const [billNumber, setBillNumber] = useState("");
  const [blNumberOptions, setBlNumberOptions] = useState<any[]>([]);
  const [blNumberLoading, setBlNumberLoading] = useState(false);
  const [gridRecord, setGridRecord] = useState<any[]>([]);

  const getPendingBillNumberOptions = async (search: string) => {
    const query = search?.trim();
    if (!query || query.length < 2) {
      setBlNumberOptions([]);
      return;
    }

    setBlNumberLoading(true);
    try {
      const results = await fetchWrapper.get(
        `get-pending-bill-numbers?search=${encodeURIComponent(query)}`
      );
      const billNumbers = results?.value?.billNumbers ?? results?.billNumbers;

      if (Array.isArray(billNumbers) && billNumbers.length > 0) {
        setBlNumberOptions(billNumbers);
      } else {
        setBlNumberOptions([]);
      }
    } catch {
      setBlNumberOptions([]);
    } finally {
      setBlNumberLoading(false);
    }
  };

  const loadInvoiceHistory = async (selectedBillNumber: string) => {
    const search = selectedBillNumber?.trim();
    if (!search) {
      dispatch(
        addNotificationAction({
          message: "Please select a valid BL Number first.",
          options: { variant: "error" },
        })
      );
      return;
    }

    setBillNumber(search);
    setLoader(true);
    try {
      const results = await fetchWrapper.get(
        `get-invoice-history-by-bill-number?billNumber=${encodeURIComponent(search)}`
      );
      const invoiceHistory = parseInvoiceHistoryResponse(results);
      const responseGatePassId = results?.value?.gatePassId ?? results?.gatePassId;
      const responseGatePassPaymentId =
        results?.value?.gatePassPaymentId ?? results?.gatePassPaymentId;

      if (results?.isSuccess === false && invoiceHistory.length === 0) {
        setGridRecord([]);
        dispatch(
          addNotificationAction({
            message: results?.errors?.[0] || "No invoice history found.",
            options: { variant: "error" },
          })
        );
        return;
      }

      setGridRecord(
        invoiceHistory.map((row: any, index: number) => ({
          ...row,
          gatePassId: row?.gatePassId ?? responseGatePassId,
          gatePassPaymentId: row?.gatePassPaymentId ?? row?.paymentId ?? responseGatePassPaymentId,
          id: row?.id ?? index,
        }))
      );

      if (invoiceHistory.length === 0) {
        dispatch(
          addNotificationAction({
            message: "No invoice history found.",
            options: { variant: "info" },
          })
        );
      }
    } catch (error: any) {
      setGridRecord([]);
      dispatch(
        addNotificationAction({
          message: error.message,
          options: { variant: "error" },
        })
      );
    } finally {
      setLoader(false);
    }
  };

  const exportHandler = async (rows: any[]) => {
    dispatch(
      addNotificationAction({
        message: "Your download will begin shortly",
        options: { variant: "success" },
      })
    );

    return rows?.map((data: any) => ({
      "BL Number": data?.billNumber || "",
      "VIR #": data?.virNo || "",
      "Container No": data?.containerNo || "",
      "Out Container No": data?.outContainerNo || "",
      "Chassis Number": data?.chassisNumbers || "",
      "Index No": data?.indexNo ?? "",
      "Index Status": data?.indexStatus || "",
      "IGMO Status": data?.igmoStatus || "",
      "Created At": data?.createdAt
        ? moment(new Date(data.createdAt)).format("DD-MM-YYYY HH:mm")
        : "",
      "Checked Out At": data?.checkedOutAt
        ? moment(new Date(data.checkedOutAt)).format("DD-MM-YYYY HH:mm")
        : "",
      "Vehicle Number": data?.vehicleNumber || "",
      "NLC Seal Number": data?.nlcSealNumber || "",
      "Weight To Be Out": data?.weightToBeOut ?? "",
    }));
  };

  const handleBlNumberSelect = async (selectedBillNumber: string) => {
    await loadInvoiceHistory(selectedBillNumber);
  };

  const handleSearch = () => {
    loadInvoiceHistory(billNumber);
  };

  const handleReset = () => {
    setBillNumber("");
    setBlNumberOptions([]);
    setGridRecord([]);
  };

  const handlePrintGatePass = useCallback(
    async (row: any) => {
      const gatePassId = row?.gatePassId;
      if (!gatePassId) {
        dispatch(
          addNotificationAction({
            message: "Gate pass id is missing for this record.",
            options: { variant: "error" },
          })
        );
        return;
      }

      const weight = String(row?.weightToBeOut ?? row?.gatePassWeight ?? "0").trim() || "0";
      const sealNoRaw = (row?.nlcSealNumber ?? "").trim();
      const sealNo = sealNoRaw ? sealNoRaw : '""';
      const idxStatus = String(row?.indexStatus ?? "").trim();
      const containerNumber = String(row?.containerNo ?? row?.containerNumber ?? "").trim();
      const indexNo = String(row?.indexNo ?? "").trim();
      const vehicleId = String(row?.vehicleId ?? "").trim();
      const gateOutContainerNumber = String(
        row?.outContainerNo ?? row?.gateOutContainerNumber ?? ""
      ).trim();
      const billNo = String(row?.billNumber ?? billNumber ?? "").trim();
      const gatePassPaymentId = row?.gatePassPaymentId ?? row?.paymentId ?? "";
      const containerChassisNumbers = buildContainerChassisNumbersFromRows(row, gridRecord);
      const chassisNumbers = containerChassisNumbers
        .map((item) => item.chassisNumber)
        .filter(Boolean)
        .join(", ");

      const gatePassExtraParams = [
        `containerChassisNumbers=${encodeURIComponent(JSON.stringify(containerChassisNumbers))}`,
        ...(chassisNumbers
          ? [
              `chassisNumber=${encodeURIComponent(chassisNumbers)}`,
              `billNumber=${encodeURIComponent(billNo)}`,
            ]
          : []),
        "toSave=false",
      ].join("&");

      setLoader(true);
      try {
        const printParams = [
          `indexNo=${encodeURIComponent(indexNo)}`,
          `virNo=${row?.virNo}`,
          `gatePassPaymentId=${gatePassPaymentId}`,
          `gatePassWeight=${encodeURIComponent(weight)}`,
          `nlcSealNumber=${encodeURIComponent(String(sealNo))}`,
          `indexStatus=${encodeURIComponent(idxStatus)}`,
          `containerNumber=${encodeURIComponent(containerNumber)}`,
          `vehicleId=${encodeURIComponent(vehicleId)}`,
          `gateOutContainerNumber=${encodeURIComponent(gateOutContainerNumber)}`,
          gatePassExtraParams,
        ].join("&");

        const fileMetaData = {
          url: `${process.env.REACT_APP_API_URL}/get-gate-pass-invoice-print/${gatePassId}?${printParams}`,
          contentType: ".pdf",
          fileName: "testPdf.pdf",
        };
        await dispatch(getPrintFile(fileMetaData, dispatch));
      } catch (error: any) {
        dispatch(
          addNotificationAction({
            message: error?.message || "Failed to print gate pass invoice.",
            options: { variant: "error" },
          })
        );
      } finally {
        setLoader(false);
      }
    },
    [billNumber, dispatch, gridRecord]
  );

  const displayColumns = useMemo(
    () =>
      columns.map((column: any) =>
        column.accessorKey === "action"
          ? {
              ...column,
              Cell: function ActionCell({ row }: any) {
                return (
                  <NewCommonButton
                    mr0
                    style={{ marginTop: 0 }}
                    icon={<PrintSharp fontSize="small" />}
                    onClick={() => handlePrintGatePass(row.original)}
                  >
                    Print
                  </NewCommonButton>
                );
              },
            }
          : column
      ),
    [handlePrintGatePass]
  );

  return (
    <>
      <Backdrop className={classes.backdrop} open={loader}>
        <CommonLoader />
      </Backdrop>

      <CommonDataGridPage
        module={moduleName}
        heading={pageHeading}
        GridDataComp={
          <CommonMaterialGrid
            columns={displayColumns}
            rows={gridRecord}
            loader={loader}
            defaultPerPageRecord={15}
            height={550}
            isExport={isExport}
            exportHandler={exportHandler}
            filename={`GatePass_Invoice_History_${billNumber || "export"}_${Date.now()}`}
          />
        }
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <CommonAutoCompleteField
              cssClassName="customTextField"
              label="BL Number"
              placeholder="Enter BL Number"
              name="billNumber"
              optionsType={blNumberOptions}
              loading={blNumberLoading}
              getOptionLabel={(option: any) => option?.billNumber?.toString() || ""}
              value={
                blNumberOptions.find(
                  (item: any) => String(item?.billNumber ?? "") === String(billNumber ?? "")
                ) || (billNumber ? { billNumber } : null)
              }
              onChange={(option: any) => {
                if (!option) {
                  setBillNumber("");
                  setBlNumberOptions([]);
                  setGridRecord([]);
                  return;
                }
                handleBlNumberSelect(String(option?.billNumber ?? ""));
              }}
              onKeyUp={(e: any) => {
                const nextLen = e.target.value.length;
                if (nextLen >= 2) {
                  getPendingBillNumberOptions(e.target.value);
                } else {
                  setBlNumberOptions([]);
                }
              }}
              onKeyDownCapture={(e: any) => {
                if (/[^A-Za-z0-9\d]/.test(e.key)) e.preventDefault();
              }}
              onPaste={(e: any) => {
                const pasted = e.clipboardData.getData("text");
                if (/[^A-Za-z0-9\d]/.test(pasted)) {
                  e.preventDefault();
                  return;
                }
                if (pasted.length >= 2) {
                  getPendingBillNumberOptions(pasted);
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <Box textAlign="right" mt={1} mb={1} className="terminalChargesSearchBtns">
              <CommonButton cssClass="customSubmitBtn" onClick={handleReset}>
                Reset
              </CommonButton>
              <CommonButton cssClass="customSubmitBtn" isGreen onClick={handleSearch}>
                Search
              </CommonButton>
            </Box>
          </Grid>
        </Grid>
      </CommonDataGridPage>
    </>
  );
}
